// Onboarding requests: the items a new department sends Ember Tracking while it
// is being set up (a member spreadsheet to import, a Drive folder of SOPs, a
// question). Each is a server-owned `onboardingRequests/{id}` doc with a status
// and an activity thread; see docs/architecture/onboarding-requests.md.
//
// Shared by the api (validation) and the department app (labels, icons). Pure —
// no firebase, no moment — and nothing in functions/ imports it, so a change
// here needs no npm publish.

const OnboardingRequestStatuses = {
    submitted: 'Submitted',
    in_progress: 'In progress',
    needs_info: 'Needs info',
    completed: 'Completed',
    declined: 'Declined',
    withdrawn: 'Withdrawn'
};

// Still waiting on somebody. The complement (completed / declined / withdrawn)
// is closed until the operator reopens it.
const OPEN_ONBOARDING_REQUEST_STATUSES = ['submitted', 'in_progress', 'needs_info'];

// What the operator may set. `withdrawn` is the department's own move, so it is
// never offered to the operator; any other status is reachable from any other,
// which is how a closed request is reopened.
const OPERATOR_ONBOARDING_REQUEST_STATUSES = ['submitted', 'in_progress', 'needs_info', 'completed', 'declined'];

// A department may pull back a request only while nobody has started on it, or
// while it is waiting on the department anyway.
const WITHDRAWABLE_ONBOARDING_REQUEST_STATUSES = ['submitted', 'needs_info'];

const OnboardingRequestKinds = {
    import: 'Records to import',
    documents: 'Documents to add',
    question: 'Question',
    other: 'Something else'
};

const LinkProviders = {
    googledrive: 'Google Drive',
    onedrive: 'OneDrive',
    dropbox: 'Dropbox',
    other: 'Link'
};

// Hostname suffixes per provider. Suffix matching (on a dot boundary) rather
// than `includes`, so `drive.google.com.evil.example` is not Google Drive.
const PROVIDER_HOSTS = {
    googledrive: ['drive.google.com', 'docs.google.com', 'sheets.google.com', 'slides.google.com'],
    onedrive: ['onedrive.live.com', '1drv.ms', 'sharepoint.com'],
    dropbox: ['dropbox.com', 'db.tt']
};

const MAX_LINK_LENGTH = 2000;

// The link as a canonical absolute http(s) URL, or null. Links are stored and
// rendered as hrefs, so anything else (javascript:, data:, a bare word) must
// never get through.
const normalizeLink = value => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_LINK_LENGTH) return null;
    // People paste "drive.google.com/…" without a scheme.
    const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    let url;
    try {
        url = new URL(candidate);
    } catch {
        return null;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (!url.hostname || !url.hostname.includes('.')) return null;
    // Credentials in a share link are a leak waiting to be copied into an email.
    if (url.username || url.password) return null;
    return url.toString();
};

// Icon/label only — the api never fetches the link.
const detectLinkProvider = value => {
    const normalized = normalizeLink(value);
    if (!normalized) return 'other';
    const host = new URL(normalized).hostname.toLowerCase();
    for (const [provider, hosts] of Object.entries(PROVIDER_HOSTS)) {
        if (hosts.some(suffix => host === suffix || host.endsWith(`.${suffix}`))) return provider;
    }
    return 'other';
};

module.exports = {
    OnboardingRequestStatuses,
    OPEN_ONBOARDING_REQUEST_STATUSES,
    OPERATOR_ONBOARDING_REQUEST_STATUSES,
    WITHDRAWABLE_ONBOARDING_REQUEST_STATUSES,
    OnboardingRequestKinds,
    LinkProviders,
    MAX_LINK_LENGTH,
    normalizeLink,
    detectLinkProvider
};
